// DOBEmailLookup.jsx
import React, { useState } from "react";
import { TextField, Button, Box, Typography } from "@mui/material";
import {
  searchDOBProfileByEmail,
  upsertDOBProfile,
} from "../../services/profileService";
import { useHeaders } from "../../HeadersContext";

const DOBEmailLookup = ({ onCancel }) => {
  const { headers } = useHeaders();
  const [email, setEmail] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US");
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleLookup = async () => {
    if (!email) return;
    setIsSearching(true);
    try {
      const lookupObject = { Email: email };
      const result = await searchDOBProfileByEmail(lookupObject, headers);
      let profileInfo = result;
      if (
        result.OwnerInformation &&
        Array.isArray(result.OwnerInformation) &&
        result.OwnerInformation.length > 0
      ) {
        const info = result.OwnerInformation[0];
        profileInfo = {
          contactGuid: info.ContactGuid,
          businessName: info.BusinessName,
          businessPhone: info.BusinessPhone,
          city: info.City,
          email: info.Email,
          firstName: info.FirstName,
          lastName: info.LastName,
          licenseList: info.LicenseList || [],
          middleName: info.MiddleName,
          mobile: info.Mobile,
          ownerType: info.OwnerType,
          state: info.State,
          street: info.Street,
          zip: info.Zip,
        };
      }
      setLookupResult(profileInfo);
    } catch (error) {
      console.error("Error during director lookup:", error);
    }
    setIsSearching(false);
  };

  const handleConfirm = async () => {
    if (!lookupResult) return;
    try {
      const profile = { ...lookupResult };
      await upsertDOBProfile(profile);
      alert("Profile saved successfully!");
    } catch (error) {
      console.error("Error saving DOB profile:", error);
      alert("Error saving DOB profile. Please try again.");
    }
    setLookupResult(null);
    setEmail("");
  };

  const handleCancel = () => {
    setLookupResult(null);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Box border="1px solid #ccc" borderRadius={1} p={2} mt={2}>
      {!lookupResult ? (
        <>
          <Typography variant="h6" gutterBottom>
            Lookup DOB Profile by Email
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              label="Email"
              value={email}
              onChange={handleEmailChange}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleLookup}
              disabled={isSearching}
            >
              {isSearching ? "Searching..." : "Lookup"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSearching}
            >
              Cancel
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Typography variant="h6" gutterBottom>
            Lookup Result
          </Typography>
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              Profile Details
            </Typography>
            <Typography variant="body1">
              <strong>Business Name:</strong>{" "}
              {lookupResult.businessName || "N/A"}
            </Typography>
            <Typography variant="body1">
              <strong>Business Phone:</strong>{" "}
              {lookupResult.businessPhone || "N/A"}
            </Typography>
            <Typography variant="body1">
              <strong>City:</strong> {lookupResult.city || "N/A"}
            </Typography>
            <Typography variant="body1">
              <strong>Email:</strong> {lookupResult.email || "N/A"}
            </Typography>
            <Typography variant="body1">
              <strong>First Name:</strong> {lookupResult.firstName || "N/A"}
            </Typography>
            <Typography variant="body1">
              <strong>Last Name:</strong> {lookupResult.lastName || "N/A"}
            </Typography>
            <Typography variant="body1">
              <strong>Last Updated:</strong>{" "}
              {formatDate(lookupResult.lastUpdated)}
            </Typography>
          </Box>
          {lookupResult.licenseList && lookupResult.licenseList.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle1" gutterBottom>
                License Information
              </Typography>
              {lookupResult.licenseList.map((lic, idx) => (
                <Box
                  key={idx}
                  mb={1}
                  p={1}
                  border="1px solid #ccc"
                  borderRadius={4}
                >
                  <Typography variant="body2">
                    <strong>Description:</strong>{" "}
                    {lic.LicenseDescription || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Number:</strong> {lic.LicenseNumber || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Type:</strong> {lic.LicenseType || "N/A"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <Box display="flex" gap={2} mt={1}>
            <Button variant="contained" onClick={handleConfirm}>
              Save Profile
            </Button>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DOBEmailLookup;
